import { Alert, AlertTitle, Button, Typography } from '@mui/material'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { Form, useForm } from '@cedarjs/forms'
import { useEffect } from 'react'
import { useMutation } from '@cedarjs/web'

import useModal from 'src/hooks/useModal'
import { SUBMIT_REFERENCE_FEEDBACK } from './queries'

import Modal from '../Modal/Modal'
import ModalActions from '../Modal/ModalActions'
import ModalContent from '../Modal/ModalContent'
import ModalHeader from '../Modal/ModalHeader'
import RadioGroup from '../MUI/RadioGroup'
import TextField from '../MUI/TextField'
import { Reference } from 'types/graphql'
import { LoadingButton } from '@mui/lab'
import toast from 'react-hot-toast'

const modalName = 'referenceFeedbackModal'

const validationSchema = Yup.object().shape({
  feedbackType: Yup.string().required('Please select a feedback type'),
  comment: Yup.string().optional(),
})

const feedbackOptions = [
  { label: 'Approve', value: 'APPROVE' },
  { label: 'Reject', value: 'REJECT' },
]

const ReferenceFeedbackModal = () => {
  const { handleClose, data, isOpen } = useModal(modalName)
  const [submitFeedback, { loading }] = useMutation(SUBMIT_REFERENCE_FEEDBACK)

  const reference = data?.reference as Reference
  const maintenanceItemId = data?.maintenanceItemId
  const isAiGenerated = !!reference?.aiReasoning

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      feedbackType: '',
      comment: '',
    },
  })

  useEffect(() => {
    if (!isOpen) {
      formMethods.reset({
        feedbackType: '',
        comment: '',
      })
    }
  }, [isOpen, formMethods])

  const onSubmit = async (formData) => {
    try {
      await submitFeedback({
        variables: {
          input: {
            referenceId: reference?.id,
            maintenanceItemId,
            feedbackType: formData.feedbackType,
            comment: formData.comment || undefined,
          },
        },
      })
      handleClose()
    } catch (error) {
      console.error('Error submitting feedback:', error)
      toast.error('Error submitting feedback')
    }
  }

  return (
    <Modal modalName={modalName} classes={{ paper: 'w-[600px]' }}>
      <ModalHeader title="Share Your Feedback" />

      <Form
        formMethods={formMethods}
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        <ModalContent>
          <div className="flex flex-col gap-4 p-4">
            <Typography variant="body1">
              We value your input! Your feedback helps us improve our reference
              system and provide better experiences for everyone.
            </Typography>
            {isAiGenerated && (
              <Alert severity="info" icon={<AutoAwesomeRoundedIcon />}>
                <AlertTitle>AI-Generated Reference</AlertTitle>
                This reference was generated using AI. Your feedback helps us
                improve our AI system and provide better references in the
                future.
              </Alert>
            )}

            <RadioGroup
              name="feedbackType"
              label="How would you rate this reference?"
              options={feedbackOptions}
              required
            />

            <TextField
              name="comment"
              label="Additional Comments"
              multiline
              rows={4}
              placeholder="Share your thoughts on what worked well or what could be improved"
            />
          </div>
        </ModalContent>

        <ModalActions>
          <Button
            fullWidth
            color="base"
            onClick={handleClose}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            fullWidth
            type="submit"
            loading={loading}
            variant="contained"
          >
            Submit Feedback
          </LoadingButton>
        </ModalActions>
      </Form>
    </Modal>
  )
}

export default ReferenceFeedbackModal
